import { Box, Menu, MenuButton, MenuItem, MenuList, Text, useTheme, VStack } from '@chakra-ui/react';
import React, { cloneElement, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { OnboardingBalloon, useServicosOnboarding } from './ServicosOnboarding';
import { isMenuLinkActive } from '@/utils';

export interface IBottomMenuChildLink {
  name: string;
  icon: React.JSX.Element;
  path: string;
  state?: Record<string, unknown>;
}

export interface IBottomMenuLink {
  name: string;
  icon: React.JSX.Element;
  path: string;
  desktopName?: string;
  hide?: boolean;
  children?: IBottomMenuChildLink[];
  state?: Record<string, unknown>;
}

function BottomMenuLink({ link }: { link: IBottomMenuLink }) {
  const { colors } = useTheme();
  const isActive = useMemo(() => isMenuLinkActive(link.path), [link]);

  return (
    <Link style={{ minWidth: 50, cursor: 'pointer' }} to={link.path} state={link.state}>
      <VStack key={link.name} spacing={0}>
        {cloneElement(link.icon, {
          transition: '0.2s',
          width: 27,
          height: 27,
          color: isActive ? colors.primaryColor : '#747474',
        })}
        <Text textAlign={'center'} whiteSpace={'nowrap'} transition={'0.2s'} color={isActive ? 'primaryColor' : '#747474'} fontWeight={'500'} fontSize={'12px'}>
          {link.name}
        </Text>
      </VStack>
    </Link>
  );
}

function BottomMenuDropdownLink({ link }: { link: IBottomMenuLink }) {
  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <Menu placement="top" strategy="fixed">
      {({ isOpen }) => (
        <Box minWidth={50}>
          <MenuButton as={Box} cursor="pointer">
            <VStack spacing={0} maxWidth="70px">
              {cloneElement(link.icon, {
                transition: '0.2s',
                width: 27,
                height: 27,
                color: isOpen ? colors.primaryColor : '#747474',
              })}
              <Text textAlign={'center'} transition={'0.2s'} color={isOpen ? colors.primaryColor : '#747474'} fontWeight={'500'} fontSize={'10px'} lineHeight={'1.2'}>
                {link.name}
              </Text>
            </VStack>
          </MenuButton>
          <MenuList
            minWidth="160px"
            borderRadius="12px"
            boxShadow="rgba(17, 12, 46, 0.15) 0px 4px 20px 0px"
            padding="4px"
            zIndex={10000}
          >
            {link.children?.map((child) => (
              <MenuItem
                key={child.name}
                icon={cloneElement(child.icon, {
                  width: 18,
                  height: 18,
                  color: colors.primaryColor,
                })}
                borderRadius="8px"
                fontSize="14px"
                fontWeight="500"
                onClick={() => navigate(child.path, { state: child.state })}
              >
                {child.name}
              </MenuItem>
            ))}
          </MenuList>
        </Box>
      )}
    </Menu>
  );
}

export function BottomMenu({ isIframeActive, links }: { isIframeActive?: boolean, links: IBottomMenuLink[] }) {
  const { isIOS } = useTheme();
  const { visible, confirm } = useServicosOnboarding();
  const servicosIndex = links.findIndex((l) => l.name === 'Serviços');
  const showOnboarding = visible && servicosIndex >= 0 && links.length > 0;
  const arrowLeft = links.length > 0 ? `${((servicosIndex + 0.5) * 100) / links.length}%` : '50%';
  const menuHeight = isIOS ? '95px' : '75px';

  return (
    <>
      {showOnboarding && (
        <Box display={{ base: 'block', lg: 'none' }}>
          <OnboardingBalloon
            text="Além dos produtos, aqui estão os serviços que você possui junto ao combo adquirido."
            onConfirm={confirm}
            arrowLeft={arrowLeft}
            menuHeight={menuHeight}
          />
        </Box>
      )}
      <Box
        display={{ base: 'flex', lg: 'none' }}
        flexWrap="wrap"
        justifyContent="space-around"
        alignItems="center"
        position={'fixed'}
        bottom="0px"
        left={'0px'}
        right={'0px'}
        backgroundColor={'rgba(255,255,255,0.95)'}
        boxShadow="rgba(17, 12, 46, 0.3) 0px 48px 100px 0px"
        backdropFilter={'blur(5px)'}
        padding="10px"
        zIndex={showOnboarding ? 1500 : 200}
        pointerEvents={isIframeActive ? 'none' : 'auto'}
        paddingBottom={isIOS ? '30px' : '10px'}
        gap="6px"
      >
        {links.map((l, i) =>
          l.children ? (
            <BottomMenuDropdownLink key={i} link={l} />
          ) : (
            <BottomMenuLink key={i} link={l} />
          )
        )}
      </Box>
    </>
  );
}
